<template>
  <div class="register-wrapper">
    <div class="noise-bg"></div>

    <!-- Background Orbs -->
    <div class="bg-orb orb1"></div>
    <div class="bg-orb orb2"></div>
    <div class="bg-orb orb3"></div>

    <!-- Header -->
    <header class="register-header">
      <a href="/" class="logo">
        <div class="logo-icon">nd</div>
        <span class="logo-text">new<span>dental</span></span>
      </a>
      <div class="header-right">
        <span class="header-hint">Já tem conta?</span>
        <a href="/login" class="header-login-btn">Entrar</a>
      </div>
    </header>

    <!-- Main Content -->
    <main class="register-main">

      <!-- Step Indicator -->
      <div class="step-indicator">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="step-item"
          :class="{
            'active': currentStep === index,
            'completed': currentStep > index
          }"
        >
          <div class="step-bubble">
            <span v-if="currentStep > index" class="step-check">✓</span>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <span class="step-label">{{ step }}</span>
          <div v-if="index < steps.length - 1" class="step-connector" :class="{ filled: currentStep > index }"></div>
        </div>
      </div>

      <!-- Form Card -->
      <transition name="slide-fade" mode="out-in">

        <!-- PASSO 1: Dados da Empresa -->
        <div v-if="currentStep === 0" key="step0" class="form-card">
          <div class="form-card-header">
            <div class="section-badge">Passo 1 de 3</div>
            <h2 class="form-title">Dados da <span class="gradient-text">Empresa</span></h2>
            <p class="form-subtitle">Informe os dados da sua clínica ou consultório para configurar sua conta.</p>
          </div>

          <!-- Logo Upload -->
          <div class="logo-upload-area" @click="triggerFileInput" :class="{ 'has-file': logoPreview }">
            <input
              ref="fileInput"
              type="file"
              accept="image/*"
              style="display:none"
              @change="handleLogoUpload"
            />
            <div v-if="!logoPreview" class="upload-placeholder">
              <div class="upload-icon">🏢</div>
              <div class="upload-text">Clique para enviar o logótipo</div>
              <div class="upload-sub">PNG, JPG ou SVG • Máx. 5MB</div>
            </div>
            <div v-else class="upload-preview">
              <img :src="logoPreview" alt="Logo preview" class="logo-preview-img" />
              <div class="upload-overlay">
                <span>Alterar</span>
              </div>
            </div>
            <div v-if="uploadLoading" class="upload-loading">
              <div class="spinner"></div>
              <span>A enviar...</span>
            </div>
          </div>
          <div v-if="errors.resourceId" class="field-error">{{ errors.resourceId }}</div>

          <div class="form-grid">
            <div class="field-group col-span-2">
              <label class="field-label">Razão Social *</label>
              <input
                v-model="form.name"
                type="text"
                class="field-input"
                :class="{ error: errors.name }"
                placeholder="Ex: Clínica Odontológica Ltda."
              />
              <div v-if="errors.name" class="field-error">{{ errors.name }}</div>
            </div>

            <div class="field-group col-span-2">
              <label class="field-label">Nome Fantasia *</label>
              <input
                v-model="form.tradeName"
                type="text"
                class="field-input"
                :class="{ error: errors.tradeName }"
                placeholder="Ex: New Dental Centro"
              />
              <div v-if="errors.tradeName" class="field-error">{{ errors.tradeName }}</div>
            </div>

            <div class="field-group col-span-2">
              <label class="field-label">CNPJ *</label>
              <input
                v-model="form.registration"
                type="text"
                class="field-input"
                :class="{ error: errors.registration }"
                placeholder="00.000.000/0000-00"
                maxlength="18"
                @input="maskCNPJ"
              />
              <div v-if="errors.registration" class="field-error">{{ errors.registration }}</div>
            </div>
          </div>

          <!-- Endereço -->
          <div class="section-divider">
            <span class="divider-label">📍 Endereço</span>
          </div>

          <div class="form-grid">
            <div class="field-group">
              <label class="field-label">CEP *</label>
              <div class="input-with-btn">
                <input
                  v-model="form.zipCode"
                  type="text"
                  class="field-input"
                  :class="{ error: errors.zipCode }"
                  placeholder="00000-000"
                  maxlength="9"
                  @input="maskCEP"
                  @blur="fetchAddressByCEP"
                />
                <button class="input-action-btn" @click="fetchAddressByCEP" :disabled="cepLoading" type="button">
                  <span v-if="cepLoading" class="spinner-sm"></span>
                  <span v-else>🔍</span>
                </button>
              </div>
              <div v-if="errors.zipCode" class="field-error">{{ errors.zipCode }}</div>
            </div>

            <div class="field-group">
              <label class="field-label">Número *</label>
              <input
                v-model="form.number"
                type="text"
                class="field-input"
                :class="{ error: errors.number }"
                placeholder="Ex: 123"
              />
              <div v-if="errors.number" class="field-error">{{ errors.number }}</div>
            </div>

            <div class="field-group col-span-2">
              <label class="field-label">Logradouro *</label>
              <input
                v-model="form.street"
                type="text"
                class="field-input"
                :class="{ error: errors.street }"
                placeholder="Nome da rua, avenida..."
              />
              <div v-if="errors.street" class="field-error">{{ errors.street }}</div>
            </div>

            <div class="field-group col-span-2">
              <label class="field-label">Bairro *</label>
              <input
                v-model="form.district"
                type="text"
                class="field-input"
                :class="{ error: errors.district }"
                placeholder="Nome do bairro"
              />
              <div v-if="errors.district" class="field-error">{{ errors.district }}</div>
            </div>

            <div class="field-group">
              <label class="field-label">Cidade *</label>
              <input
                v-model="form.city"
                type="text"
                class="field-input"
                :class="{ error: errors.city }"
                placeholder="Ex: São Paulo"
              />
              <div v-if="errors.city" class="field-error">{{ errors.city }}</div>
            </div>

            <div class="field-group">
              <label class="field-label">Estado *</label>
              <select v-model="form.state" class="field-input field-select" :class="{ error: errors.state }">
                <option value="">Selecione</option>
                <option v-for="uf in estados" :key="uf" :value="uf">{{ uf }}</option>
              </select>
              <div v-if="errors.state" class="field-error">{{ errors.state }}</div>
            </div>

            <div class="field-group">
              <label class="field-label">Latitude</label>
              <input
                v-model.number="form.latitude"
                type="number"
                step="0.000001"
                class="field-input"
                placeholder="-23.550520"
              />
            </div>

            <div class="field-group">
              <label class="field-label">Longitude</label>
              <input
                v-model.number="form.longitude"
                type="number"
                step="0.000001"
                class="field-input"
                placeholder="-46.633308"
              />
            </div>
          </div>

          <div class="form-actions">
            <div></div>
            <button class="btn-next" @click="nextStep" :disabled="step0Loading">
              <span v-if="step0Loading" class="spinner-sm"></span>
              <span v-else>Continuar ›</span>
            </button>
          </div>
        </div>

        <!-- PASSO 2: Dados do Responsável -->
        <div v-else-if="currentStep === 1" key="step1" class="form-card">
          <div class="form-card-header">
            <div class="section-badge">Passo 2 de 3</div>
            <h2 class="form-title">Dados do <span class="gradient-text">Responsável</span></h2>
            <p class="form-subtitle">Informe os dados do dentista ou administrador responsável pela conta.</p>
          </div>

          <div class="company-summary-card">
            <div class="summary-icon">🏢</div>
            <div>
              <div class="summary-name">{{ form.tradeName || form.name }}</div>
              <div class="summary-sub">{{ form.city }}, {{ form.state }} • CNPJ {{ form.registration }}</div>
            </div>
            <div class="summary-check">✓</div>
          </div>

          <div class="form-grid">
            <div class="field-group col-span-2">
              <label class="field-label">Nome Completo *</label>
              <input
                v-model="user.name"
                type="text"
                class="field-input"
                :class="{ error: errors.userName }"
                placeholder="Ex: Dr. Alexandre Tubino"
              />
              <div v-if="errors.userName" class="field-error">{{ errors.userName }}</div>
            </div>

            <div class="field-group col-span-2">
              <label class="field-label">E-mail *</label>
              <input
                v-model="user.email"
                type="email"
                class="field-input"
                :class="{ error: errors.email }"
                placeholder="dentista@clinica.com.br"
              />
              <div v-if="errors.email" class="field-error">{{ errors.email }}</div>
            </div>

            <div class="field-group col-span-2">
              <label class="field-label">Tipo de Usuário *</label>
              <div class="type-selector">
                <button
                  v-for="(opt, i) in userTypes"
                  :key="i"
                  type="button"
                  class="type-option"
                  :class="{ active: user.type === opt.value }"
                  @click="user.type = opt.value"
                >
                  <span class="type-icon">{{ opt.icon }}</span>
                  <span class="type-label">{{ opt.label }}</span>
                </button>
              </div>
              <div v-if="errors.type" class="field-error">{{ errors.type }}</div>
            </div>
          </div>

          <div class="section-divider">
            <span class="divider-label">🔐 Credenciais de Acesso</span>
          </div>

          <div class="form-grid">
            <div class="field-group col-span-2">
              <label class="field-label">Utilizador (login) *</label>
              <input
                v-model="user.credentials.username"
                type="text"
                class="field-input"
                :class="{ error: errors.username }"
                placeholder="nome.sobrenome ou e-mail"
                autocomplete="username"
              />
              <div v-if="errors.username" class="field-error">{{ errors.username }}</div>
            </div>

            <div class="field-group">
              <label class="field-label">Senha *</label>
              <div class="input-password">
                <input
                  v-model="user.credentials.password"
                  :type="showPassword ? 'text' : 'password'"
                  class="field-input"
                  :class="{ error: errors.password }"
                  placeholder="Mínimo 8 caracteres"
                  autocomplete="new-password"
                />
                <button type="button" class="password-toggle" @click="showPassword = !showPassword">
                  {{ showPassword ? '🙈' : '👁️' }}
                </button>
              </div>
              <div v-if="errors.password" class="field-error">{{ errors.password }}</div>
            </div>

            <div class="field-group">
              <label class="field-label">Confirmar Senha *</label>
              <div class="input-password">
                <input
                  v-model="user.confirmPassword"
                  :type="showConfirm ? 'text' : 'password'"
                  class="field-input"
                  :class="{ error: errors.confirmPassword }"
                  placeholder="Repita a senha"
                  autocomplete="new-password"
                />
                <button type="button" class="password-toggle" @click="showConfirm = !showConfirm">
                  {{ showConfirm ? '🙈' : '👁️' }}
                </button>
              </div>
              <div v-if="errors.confirmPassword" class="field-error">{{ errors.confirmPassword }}</div>
            </div>
          </div>

          <!-- Password strength indicator -->
          <div v-if="user.credentials.password" class="password-strength">
            <div class="strength-bars">
              <div
                v-for="n in 4"
                :key="n"
                class="strength-bar"
                :class="{ active: passwordStrength >= n, [`level-${passwordStrength}`]: passwordStrength >= n }"
              ></div>
            </div>
            <span class="strength-label" :class="`strength-text-${passwordStrength}`">{{ strengthLabel }}</span>
          </div>

          <div class="form-actions">
            <button class="btn-back" @click="currentStep--">‹ Voltar</button>
            <button class="btn-next" @click="nextStep">Continuar ›</button>
          </div>
        </div>

        <!-- PASSO 3: Revisão & Confirmação -->
        <div v-else-if="currentStep === 2" key="step2" class="form-card">
          <div class="form-card-header">
            <div class="section-badge">Passo 3 de 3</div>
            <h2 class="form-title">Confirme seus <span class="gradient-text">dados</span></h2>
            <p class="form-subtitle">Revise as informações antes de criar a sua conta.</p>
          </div>

          <div class="review-grid">
            <!-- Empresa -->
            <div class="review-block">
              <div class="review-block-header">
                <span class="review-block-icon">🏢</span>
                <span class="review-block-title">Empresa</span>
                <button class="review-edit" @click="currentStep = 0">Editar</button>
              </div>
              <div class="review-rows">
                <div class="review-row"><span class="rr-label">Razão Social</span><span class="rr-value">{{ form.name }}</span></div>
                <div class="review-row"><span class="rr-label">Nome Fantasia</span><span class="rr-value">{{ form.tradeName }}</span></div>
                <div class="review-row"><span class="rr-label">CNPJ</span><span class="rr-value">{{ form.registration }}</span></div>
                <div class="review-row"><span class="rr-label">Endereço</span><span class="rr-value">{{ form.street }}, {{ form.number }} — {{ form.district }}</span></div>
                <div class="review-row"><span class="rr-label">Cidade/UF</span><span class="rr-value">{{ form.city }} / {{ form.state }}</span></div>
                <div class="review-row"><span class="rr-label">CEP</span><span class="rr-value">{{ form.zipCode }}</span></div>
              </div>
            </div>

            <!-- Responsável -->
            <div class="review-block">
              <div class="review-block-header">
                <span class="review-block-icon">👤</span>
                <span class="review-block-title">Responsável</span>
                <button class="review-edit" @click="currentStep = 1">Editar</button>
              </div>
              <div class="review-rows">
                <div class="review-row"><span class="rr-label">Nome</span><span class="rr-value">{{ user.name }}</span></div>
                <div class="review-row"><span class="rr-label">E-mail</span><span class="rr-value">{{ user.email }}</span></div>
                <div class="review-row"><span class="rr-label">Tipo</span><span class="rr-value">{{ userTypes.find(t => t.value === user.type)?.label }}</span></div>
                <div class="review-row"><span class="rr-label">Login</span><span class="rr-value">{{ user.credentials.username }}</span></div>
                <div class="review-row"><span class="rr-label">Senha</span><span class="rr-value">••••••••</span></div>
              </div>
            </div>
          </div>

          <div v-if="submitError" class="submit-error">
            <span class="error-icon">⚠️</span>
            {{ submitError }}
          </div>

          <div class="form-actions">
            <button class="btn-back" @click="currentStep--">‹ Voltar</button>
            <button class="btn-submit" @click="handleSubmit" :disabled="submitting">
              <span v-if="submitting" class="spinner-sm white"></span>
              <span v-else>🦷 Criar Conta</span>
            </button>
          </div>
        </div>

        <!-- PASSO 4: Sucesso -->
        <div v-else-if="currentStep === 3" key="step3" class="form-card success-card">
          <div class="success-animation">
            <div class="success-circle">
              <span class="success-check">✓</span>
            </div>
            <div class="success-rings">
              <div class="ring ring1"></div>
              <div class="ring ring2"></div>
              <div class="ring ring3"></div>
            </div>
          </div>
          <h2 class="success-title">Conta criada<br/><span class="gradient-text">com sucesso!</span></h2>
          <p class="success-sub">Bem-vindo ao New Dental, <strong>{{ user.name }}</strong>. A sua clínica <strong>{{ form.tradeName }}</strong> está pronta. Escolha o seu plano para continuar.</p>
          <div class="success-actions">
            <button class="btn-next" @click="goToPayment">Escolher Plano ›</button>
          </div>
        </div>

      </transition>
    </main>
  </div>
</template>

<script>
function getApiUrl() {
  const runtimeVariables = window.variables || {};
  return runtimeVariables.API || process.env.VUE_APP_API_URL || 'http://localhost:3001';
}

export default {
  name: 'RegisterPlan',

  data() {
    return {
      currentStep: 0,
      steps: ['Empresa', 'Responsável', 'Confirmação'],
      showPassword: false,
      showConfirm: false,
      logoPreview: null,
      uploadLoading: false,
      cepLoading: false,
      step0Loading: false,
      submitting: false,
      submitError: '',

      form: {
        name: '',
        tradeName: '',
        registration: '',
        street: '',
        district: '',
        number: '',
        city: '',
        state: '',
        zipCode: '',
        latitude: null,
        longitude: null,
        resourceId: null,
      },

      user: {
        name: '',
        email: '',
        type: 1,
        credentials: {
          username: '',
          password: '',
        },
        confirmPassword: '',
      },

      errors: {},

      estados: [
        'AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
        'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'
      ],

      userTypes: [
        { value: 1, icon: '🦷', label: 'Dentista' },
        { value: 2, icon: '👔', label: 'Administrador' },
        { value: 3, icon: '🏥', label: 'Clínica' },
      ],
    };
  },

  computed: {
    passwordStrength() {
      const p = this.user.credentials.password;
      if (!p) return 0;
      let score = 0;
      if (p.length >= 8) score++;
      if (/[A-Z]/.test(p)) score++;
      if (/[0-9]/.test(p)) score++;
      if (/[^A-Za-z0-9]/.test(p)) score++;
      return score;
    },
    strengthLabel() {
      const labels = ['', 'Fraca', 'Razoável', 'Boa', 'Forte'];
      return labels[this.passwordStrength] || '';
    },
  },

  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async handleLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Preview
      const reader = new FileReader();
      reader.onload = (e) => { this.logoPreview = e.target.result; };
      reader.readAsDataURL(file);

      // Upload to /resources
      this.uploadLoading = true;
      try {
        const formData = new FormData();
        formData.append('file', file);

        const apiUrl = getApiUrl();
        const response = await fetch(`${apiUrl}/resources`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) throw new Error('Erro ao enviar o logótipo.');

        const payload = await response.json();
        this.form.resourceId = (payload && payload.data ? payload.data.id : payload.id);
        this.$delete(this.errors, 'resourceId');
      } catch (err) {
        this.errors = { ...this.errors, resourceId: err.message || 'Falha ao enviar o logótipo. Tente novamente.' };
        this.logoPreview = null;
        this.form.resourceId = null;
      } finally {
        this.uploadLoading = false;
      }
    },

    maskCNPJ(event) {
      let v = event.target.value.replace(/\D/g, '');
      v = v.replace(/^(\d{2})(\d)/, '$1.$2');
      v = v.replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3');
      v = v.replace(/\.(\d{3})(\d)/, '.$1/$2');
      v = v.replace(/(\d{4})(\d)/, '$1-$2');
      this.form.registration = v;
    },

    maskCEP(event) {
      let v = event.target.value.replace(/\D/g, '');
      v = v.replace(/^(\d{5})(\d)/, '$1-$2');
      this.form.zipCode = v;
    },

    async fetchAddressByCEP() {
      const cep = this.form.zipCode.replace(/\D/g, '');
      if (cep.length !== 8) return;

      this.cepLoading = true;
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        if (data.erro) throw new Error('CEP não encontrado.');

        this.form.street   = data.logradouro || this.form.street;
        this.form.district = data.bairro      || this.form.district;
        this.form.city     = data.localidade  || this.form.city;
        this.form.state    = data.uf          || this.form.state;
      } catch (err) {
        this.errors = { ...this.errors, zipCode: err.message };
      } finally {
        this.cepLoading = false;
      }
    },

    validateStep0() {
      const e = {};
      if (!this.form.name.trim())         e.name         = 'Razão Social é obrigatória.';
      if (!this.form.tradeName.trim())     e.tradeName    = 'Nome Fantasia é obrigatório.';
      if (!this.form.registration.trim()) e.registration = 'CNPJ é obrigatório.';
      if (!this.form.zipCode.trim())      e.zipCode      = 'CEP é obrigatório.';
      if (!this.form.street.trim())       e.street       = 'Logradouro é obrigatório.';
      if (!this.form.number.trim())       e.number       = 'Número é obrigatório.';
      if (!this.form.district.trim())     e.district     = 'Bairro é obrigatório.';
      if (!this.form.city.trim())         e.city         = 'Cidade é obrigatória.';
      if (!this.form.state)               e.state        = 'Estado é obrigatório.';
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    validateStep1() {
      const e = {};
      if (!this.user.name.trim())                     e.userName        = 'Nome é obrigatório.';
      if (!this.user.email.trim())                    e.email           = 'E-mail é obrigatório.';
      else if (!/\S+@\S+\.\S+/.test(this.user.email)) e.email          = 'E-mail inválido.';
      if (!this.user.type)                            e.type            = 'Selecione um tipo.';
      if (!this.user.credentials.username.trim())     e.username        = 'Login é obrigatório.';
      if (!this.user.credentials.password)            e.password        = 'Senha é obrigatória.';
      else if (this.user.credentials.password.length < 8) e.password   = 'Senha deve ter pelo menos 8 caracteres.';
      if (this.user.credentials.password !== this.user.confirmPassword) e.confirmPassword = 'As senhas não coincidem.';
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    nextStep() {
      if (this.currentStep === 0 && this.validateStep0()) {
        this.currentStep = 1;
      } else if (this.currentStep === 1 && this.validateStep1()) {
        this.currentStep = 2;
      }
    },

    goToPayment() {
      const query = {};
      if (this.$route.query.planId) query.planId = this.$route.query.planId;
      if (this.$route.query.billing) query.billing = this.$route.query.billing;
      if (this._registeredCompanyId) query.companyId = this._registeredCompanyId;
      this.$router.push({ path: '/pagamento', query });
    },

    async handleSubmit() {
      this.submitting = true;
      this.submitError = '';

      try {
        const apiUrl = getApiUrl();

        const payload = {
          company: {
            name:         this.form.name,
            tradeName:    this.form.tradeName,
            registration: this.form.registration,
            street:       this.form.street,
            district:     this.form.district,
            number:       this.form.number,
            city:         this.form.city,
            state:        this.form.state,
            zipCode:      this.form.zipCode,
            latitude:     this.form.latitude  || 0,
            longitude:    this.form.longitude || 0,
            resourceId:   this.form.resourceId,
          },
          user: {
            name:        this.user.name,
            email:       this.user.email,
            type:        this.user.type,
            credentials: [
              {
                username: this.user.credentials.username,
                password: this.user.credentials.password,
              },
            ],
          },
        };

        const response = await fetch(`${apiUrl}/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || err?.data?.message || 'Erro ao concluir o cadastro.');
        }

        const result = await response.json();
        const companyId = result?.data?.companyId;

        // Sucesso — avança para o passo de confirmação e vai para o pagamento
        this.currentStep = 3;
        this._registeredCompanyId = companyId;
      } catch (err) {
        this.submitError = err.message || 'Ocorreu um erro inesperado. Tente novamente.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* ── VARIÁVEIS ── */
$primary:    #8E44AD;
$tertiary:   #9D4EBE;
$quaternary: #862AAC;
$dark:       #0D0618;
$dark2:      #160D22;
$glass:      rgba(255,255,255,0.06);
$glass-border: rgba(255,255,255,0.12);

@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Syne:wght@700;800&display=swap');

/* ── WRAPPER ── */
.register-wrapper {
  font-family: 'Plus Jakarta Sans', sans-serif;
  background: $dark;
  color: #fff;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

.noise-bg {
  position: fixed; inset: 0; pointer-events: none; z-index: 0; opacity: 0.4;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
}

.bg-orb { position: fixed; border-radius: 50%; filter: blur(90px); pointer-events: none; z-index: 0; }
.orb1 { width: 700px; height: 700px; background: radial-gradient(circle, rgba(142,68,173,.3) 0%, transparent 70%); top: -200px; left: -250px; }
.orb2 { width: 500px; height: 500px; background: radial-gradient(circle, rgba(134,42,172,.2) 0%, transparent 70%); bottom: -100px; right: -200px; }
.orb3 { width: 350px; height: 350px; background: radial-gradient(circle, rgba(157,78,190,.15) 0%, transparent 70%); top: 50%; left: 60%; }

/* ── HEADER ── */
.register-header {
  position: sticky; top: 0; z-index: 100;
  padding: 16px 40px;
  background: rgba(13,6,24,0.8); backdrop-filter: blur(20px);
  border-bottom: 1px solid $glass-border;
  display: flex; align-items: center; justify-content: space-between;
}

.logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
.logo-icon { width: 36px; height: 36px; background: linear-gradient(135deg, $primary, $tertiary); border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 13px; color: #fff; box-shadow: 0 4px 16px rgba(142,68,173,.5); }
.logo-text { font-family: 'Syne', sans-serif; font-size: 19px; font-weight: 700; color: #fff; span { color: $tertiary; } }

.header-right { display: flex; align-items: center; gap: 14px; }
.header-hint { font-size: 13px; color: rgba(255,255,255,.5); }
.header-login-btn { padding: 8px 20px; border: 1px solid rgba(142,68,173,.4); border-radius: 100px; font-size: 13px; font-weight: 600; color: #c77dff; text-decoration: none; transition: all .2s; &:hover { background: rgba(142,68,173,.12); } }

/* ── MAIN ── */
.register-main {
  position: relative; z-index: 1;
  max-width: 760px; margin: 0 auto;
  padding: 48px 24px 80px;
}

/* ── STEP INDICATOR ── */
.step-indicator {
  display: flex; align-items: center; justify-content: center;
  gap: 0; margin-bottom: 48px;
}

.step-item {
  display: flex; align-items: center; gap: 0;
  &:last-child .step-connector { display: none; }
}

.step-bubble {
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 700; flex-shrink: 0; z-index: 1;
  border: 2px solid rgba(255,255,255,.15);
  color: rgba(255,255,255,.4);
  background: rgba(255,255,255,.05);
  transition: all .4s ease;

  .step-item.active & {
    border-color: $primary; background: rgba(142,68,173,.2); color: #c77dff;
    box-shadow: 0 0 0 4px rgba(142,68,173,.15);
  }
  .step-item.completed & {
    border-color: $primary; background: $primary; color: #fff;
  }
}

.step-label {
  font-size: 11px; font-weight: 600; margin-left: 8px;
  color: rgba(255,255,255,.3);
  text-transform: uppercase; letter-spacing: .6px;
  transition: color .3s;
  .step-item.active & { color: #c77dff; }
  .step-item.completed & { color: rgba(255,255,255,.6); }
}

.step-connector {
  width: 60px; height: 2px;
  background: rgba(255,255,255,.1);
  margin: 0 8px; flex-shrink: 0;
  transition: background .4s;
  &.filled { background: linear-gradient(90deg, $primary, $quaternary); }
}

/* ── FORM CARD ── */
.form-card {
  background: rgba(22,13,34,.8);
  border: 1px solid $glass-border;
  border-radius: 28px;
  padding: 40px 44px;
  backdrop-filter: blur(16px);
  box-shadow: 0 40px 100px rgba(0,0,0,.4), inset 0 1px 0 rgba(255,255,255,.06);
  @media (max-width: 600px) { padding: 28px 20px; }
}

.form-card-header { margin-bottom: 32px; }
.section-badge {
  display: inline-block;
  background: rgba(142,68,173,.15); border: 1px solid rgba(142,68,173,.3);
  color: #c77dff; font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 1px; padding: 4px 12px; border-radius: 100px; margin-bottom: 12px;
}
.form-title { font-family: 'Syne', sans-serif; font-size: clamp(26px,4vw,36px); font-weight: 800; letter-spacing: -1px; margin-bottom: 8px; }
.form-subtitle { font-size: 14px; color: rgba(255,255,255,.55); line-height: 1.7; }
.gradient-text { background: linear-gradient(135deg, #c77dff, $quaternary); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }

/* ── LOGO UPLOAD ── */
.logo-upload-area {
  border: 2px dashed rgba(142,68,173,.35);
  border-radius: 16px; padding: 28px;
  text-align: center; cursor: pointer;
  transition: all .3s; margin-bottom: 8px;
  position: relative; overflow: hidden;
  &:hover { border-color: rgba(142,68,173,.7); background: rgba(142,68,173,.06); }
  &.has-file { border-style: solid; border-color: rgba(142,68,173,.5); padding: 0; height: 110px; }
}
.upload-placeholder {}
.upload-icon { font-size: 28px; margin-bottom: 8px; }
.upload-text { font-size: 14px; font-weight: 600; color: rgba(255,255,255,.8); margin-bottom: 4px; }
.upload-sub { font-size: 12px; color: rgba(255,255,255,.35); }
.upload-preview { position: relative; height: 110px; display: flex; align-items: center; justify-content: center; }
.logo-preview-img { max-height: 100%; max-width: 100%; object-fit: contain; border-radius: 14px; }
.upload-overlay { position: absolute; inset: 0; background: rgba(0,0,0,.6); display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity .2s; border-radius: 14px; font-size: 13px; font-weight: 600; color: #fff; .logo-upload-area:hover & { opacity: 1; } }
.upload-loading { position: absolute; inset: 0; background: rgba(13,6,24,.8); display: flex; align-items: center; justify-content: center; gap: 10px; font-size: 13px; color: rgba(255,255,255,.7); border-radius: 14px; }

/* ── FORM GRID ── */
.form-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
  @media (max-width: 520px) { grid-template-columns: 1fr; }
}
.col-span-2 { grid-column: span 2; @media (max-width: 520px) { grid-column: span 1; } }

.field-group { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 12px; font-weight: 600; color: rgba(255,255,255,.55); text-transform: uppercase; letter-spacing: .6px; }

.field-input {
  background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.1); border-radius: 12px;
  padding: 12px 16px; font-size: 14px; color: #fff; font-family: inherit; width: 100%; transition: all .2s;
  outline: none; box-sizing: border-box;
  &::placeholder { color: rgba(255,255,255,.25); }
  &:focus { border-color: rgba(142,68,173,.6); background: rgba(142,68,173,.08); box-shadow: 0 0 0 3px rgba(142,68,173,.12); }
  &.error { border-color: rgba(255,80,80,.5); background: rgba(255,80,80,.05); }
}

.field-select { cursor: pointer; appearance: none; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(255,255,255,0.3)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; padding-right: 38px; }
.field-select option { background: $dark2; color: #fff; }

.field-error { font-size: 11px; color: #ff7070; font-weight: 600; margin-top: 2px; }

/* ── INPUT UTILS ── */
.input-with-btn { display: flex; gap: 8px; }
.input-action-btn { background: rgba(142,68,173,.2); border: 1px solid rgba(142,68,173,.3); border-radius: 12px; width: 44px; flex-shrink: 0; cursor: pointer; font-size: 14px; transition: all .2s; color: #fff; display: flex; align-items: center; justify-content: center; &:hover { background: rgba(142,68,173,.35); } &:disabled { opacity: .5; cursor: default; } }
.input-with-btn .field-input { flex: 1; }

.input-password { position: relative; }
.input-password .field-input { padding-right: 44px; }
.password-toggle { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; font-size: 16px; padding: 0; }

/* ── PASSWORD STRENGTH ── */
.password-strength { display: flex; align-items: center; gap: 10px; margin-top: 8px; margin-bottom: 4px; }
.strength-bars { display: flex; gap: 4px; }
.strength-bar { width: 42px; height: 4px; border-radius: 100px; background: rgba(255,255,255,.1); transition: background .3s; &.active.level-1 { background: #ff5252; } &.active.level-2 { background: #ffab40; } &.active.level-3 { background: #69f0ae; } &.active.level-4 { background: #00c878; } }
.strength-label { font-size: 11px; font-weight: 700; }
.strength-text-1 { color: #ff5252; } .strength-text-2 { color: #ffab40; } .strength-text-3 { color: #69f0ae; } .strength-text-4 { color: #00c878; }

/* ── SECTION DIVIDER ── */
.section-divider { margin: 24px 0 16px; display: flex; align-items: center; gap: 12px; }
.section-divider::before, .section-divider::after { content: ''; flex: 1; height: 1px; background: rgba(255,255,255,.08); }
.divider-label { font-size: 11px; font-weight: 700; color: rgba(255,255,255,.4); text-transform: uppercase; letter-spacing: .8px; white-space: nowrap; }

/* ── COMPANY SUMMARY ── */
.company-summary-card {
  display: flex; align-items: center; gap: 14px;
  background: rgba(142,68,173,.1); border: 1px solid rgba(142,68,173,.25);
  border-radius: 14px; padding: 14px 18px; margin-bottom: 24px;
}
.summary-icon { font-size: 22px; }
.summary-name { font-size: 14px; font-weight: 700; color: #fff; }
.summary-sub { font-size: 12px; color: rgba(255,255,255,.45); }
.summary-check { margin-left: auto; width: 24px; height: 24px; background: rgba(0,200,120,.15); border: 1px solid rgba(0,200,120,.3); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #00c878; }

/* ── TYPE SELECTOR ── */
.type-selector { display: flex; gap: 8px; flex-wrap: wrap; }
.type-option {
  flex: 1; min-width: 120px;
  background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.1);
  border-radius: 12px; padding: 12px 10px;
  display: flex; align-items: center; gap: 8px;
  cursor: pointer; transition: all .2s; color: rgba(255,255,255,.6); font-family: inherit;
  &.active { background: rgba(142,68,173,.2); border-color: rgba(142,68,173,.5); color: #c77dff; }
  &:hover:not(.active) { border-color: rgba(255,255,255,.2); background: rgba(255,255,255,.07); }
}
.type-icon { font-size: 18px; }
.type-label { font-size: 13px; font-weight: 600; }

/* ── REVIEW GRID ── */
.review-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 28px; @media (max-width: 600px) { grid-template-columns: 1fr; } }
.review-block { background: rgba(255,255,255,.04); border: 1px solid $glass-border; border-radius: 16px; padding: 18px; }
.review-block-header { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; }
.review-block-icon { font-size: 18px; }
.review-block-title { font-family: 'Syne', sans-serif; font-size: 14px; font-weight: 700; color: #fff; flex: 1; }
.review-edit { background: none; border: none; cursor: pointer; font-size: 11px; font-weight: 700; color: #c77dff; text-transform: uppercase; letter-spacing: .6px; padding: 0; font-family: inherit; &:hover { text-decoration: underline; } }
.review-rows { display: flex; flex-direction: column; gap: 8px; }
.review-row { display: flex; gap: 8px; }
.rr-label { font-size: 11px; color: rgba(255,255,255,.35); font-weight: 600; min-width: 90px; }
.rr-value { font-size: 12px; color: rgba(255,255,255,.8); font-weight: 500; word-break: break-all; }

/* ── SUBMIT ERROR ── */
.submit-error { background: rgba(255,80,80,.08); border: 1px solid rgba(255,80,80,.2); border-radius: 12px; padding: 14px 16px; display: flex; align-items: center; gap: 10px; font-size: 13px; color: #ff9090; margin-bottom: 20px; }
.error-icon { font-size: 16px; }

/* ── FORM ACTIONS ── */
.form-actions { display: flex; align-items: center; justify-content: space-between; margin-top: 32px; }

.btn-back { background: rgba(255,255,255,.06); border: 1px solid rgba(255,255,255,.12); color: rgba(255,255,255,.7); padding: 13px 26px; border-radius: 100px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; transition: all .2s; &:hover { background: rgba(255,255,255,.1); color: #fff; } }
.btn-next { background: linear-gradient(135deg, $primary, $quaternary); color: #fff; padding: 13px 32px; border-radius: 100px; font-size: 14px; font-weight: 700; border: none; cursor: pointer; font-family: inherit; transition: all .3s; box-shadow: 0 8px 24px rgba(142,68,173,.4); display: flex; align-items: center; gap: 8px; text-decoration: none; &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(142,68,173,.6); } &:disabled { opacity: .6; cursor: not-allowed; } }
.btn-submit { background: linear-gradient(135deg, $primary, $quaternary); color: #fff; padding: 13px 36px; border-radius: 100px; font-size: 15px; font-weight: 700; border: none; cursor: pointer; font-family: inherit; transition: all .3s; box-shadow: 0 8px 24px rgba(142,68,173,.4); display: flex; align-items: center; gap: 8px; &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(142,68,173,.6); } &:disabled { opacity: .6; cursor: not-allowed; } }

/* ── SPINNERS ── */
.spinner { width: 28px; height: 28px; border: 3px solid rgba(142,68,173,.3); border-top-color: $primary; border-radius: 50%; animation: spin .8s linear infinite; margin: 0 auto; }
.spinner-sm { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: spin .8s linear infinite; &.white { border-top-color: #fff; } }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── SUCCESS ── */
.success-card { text-align: center; padding: 60px 44px; }
.success-animation { position: relative; width: 100px; height: 100px; margin: 0 auto 32px; }
.success-circle { width: 80px; height: 80px; background: linear-gradient(135deg, $primary, $quaternary); border-radius: 50%; display: flex; align-items: center; justify-content: center; position: relative; z-index: 2; margin: 10px auto 0; box-shadow: 0 12px 36px rgba(142,68,173,.5); animation: pop-in .5s cubic-bezier(0.175,0.885,0.32,1.275); }
.success-check { font-size: 30px; color: #fff; }
.success-rings { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
.ring { position: absolute; border-radius: 50%; border: 1px solid rgba(142,68,173,.3); animation: ring-expand 1.5s ease-out infinite; }
.ring1 { width: 90px; height: 90px; animation-delay: 0s; }
.ring2 { width: 110px; height: 110px; animation-delay: .3s; }
.ring3 { width: 130px; height: 130px; animation-delay: .6s; }
@keyframes ring-expand { 0% { transform: scale(.6); opacity: .8; } 100% { transform: scale(1.3); opacity: 0; } }
@keyframes pop-in { 0% { transform: scale(0); } 60% { transform: scale(1.1); } 100% { transform: scale(1); } }
.success-title { font-family: 'Syne', sans-serif; font-size: clamp(28px,4vw,40px); font-weight: 800; letter-spacing: -1px; margin-bottom: 16px; }
.success-sub { font-size: 15px; color: rgba(255,255,255,.6); line-height: 1.7; max-width: 400px; margin: 0 auto 40px; }
.success-actions { display: flex; justify-content: center; }

/* ── SLIDE FADE TRANSITION ── */
.slide-fade-enter-active, .slide-fade-leave-active { transition: all .35s ease; }
.slide-fade-enter-from { opacity: 0; transform: translateX(24px); }
.slide-fade-leave-to { opacity: 0; transform: translateX(-24px); }
</style>
